import React, { useState } from "react";
import { FeedbackProps, Launcher, Proactive } from "@tomis-tech/chat-ui";

import { Announcement } from "@/components/Announcement";
import { getChatApi } from "@/chatApi";
import {
  useAnnouncementDelay,
  useChatWidget,
  useRuntime,
  useToast,
} from "@/hooks";
import { getChatDomIds } from "@/namespace";
import {
  dismissAnnouncement,
  readDismissedAnnouncementIds,
} from "@/utils/dismiss";
import { trackEvent } from "@/tracking";
import { ChatContainer, Container, LauncherContainer } from "./styled";
import { ChatOpen } from "../ChatOpen";
import {
  ChatWidgetConfiguration,
  ChatWidgetHandoffStatus,
  ChatWidgetTurn,
} from "@/types";

export interface ChatWidgetProps {
  /** Callback function to run after mount and the chat API is initialized */
  onReady: () => any;
  /** Chat widget configuration stored in database */
  config: ChatWidgetConfiguration;
  /** If `true` load chat widget inside a <div> on the page */
  embed: boolean;
  /** Feedback mode for chat widget */
  feedback?: FeedbackProps["mode"];
  /** If `true`, mark conversations as test_mode in database. If true, prevents product analytics */
  test_mode?: boolean;
  /** Initial conversation history if loading existing conversation */
  conversationHistory?: ChatWidgetTurn[];
  /** Live-chat handoff state for a restored conversation, from load */
  handoffStatus?: ChatWidgetHandoffStatus;
  /** Initial conversation-level forms from load */
  forms?: import("@/types").FormInstance[];
}

/**
 * Entire Chat Widget, including closed and open views.
 *
 * This component is used for CHAT mode where users can interact with the chatbot.
 * For form-only mode, use FormOnlyWidget instead.
 */
export const ChatWidget: React.FC<React.PropsWithChildren<ChatWidgetProps>> = ({
  onReady,
  config,
  embed = false,
  feedback = "disabled",
  test_mode = false,
  conversationHistory = [],
  handoffStatus,
  forms: initialForms = [],
}) => {
  const {
    isOpen,
    isHidden,
    proactiveMessages,
    closeWidget,
    handleOpenAndLaunch,
    launcherStyle,
    chatWindowStyle,
  } = useChatWidget({ config, embed, onReady });

  // Initialize toast for error notifications
  const { showToast } = useToast();

  // Initialize runtime with server state management
  const {
    sendMessage: handleSend,
    restartSession,
    turns,
    isLoading,
    forms,
    activeFormKey,
    closeForm,
    updateFormValues,
    submitForm,
    agentTyping,
    notifyTyping,
  } = useRuntime({
    config,
    initialConversationHistory: conversationHistory,
    initialHandoffStatus: handoffStatus,
    siteId: getChatApi().chat.id || "",
    initialForms,
    feedbackMode: feedback,
    onError: (message) => showToast(message, { variant: "error" }),
    onSuccess: (message) => showToast(message, { variant: "success" }),
  });

  const chat = getChatApi().chat;
  chat.feedback = feedback;
  chat.test_mode = test_mode;

  const siteId = chat.id || "";
  const [dismissedIds, setDismissedIds] = useState<number[]>(() =>
    readDismissedAnnouncementIds(siteId),
  );

  /**
   * Get the active announcement for the current page.
   */
  function getActiveAnnouncement() {
    return (config.announcements ?? []).find(
      (item) => !dismissedIds.includes(item.id),
    );
  }

  const announcement = getActiveAnnouncement();
  /*
   * The wait starts at page load.
   * A new announcement after dismiss starts its own wait.
   * This prevents the same announcement from being shown multiple times.
   */
  const announcementReady = useAnnouncementDelay(
    announcement?.delaySeconds,
    announcement?.id,
  );

  const onDismiss = () => {
    if (!announcement) {
      return;
    }

    setDismissedIds(dismissAnnouncement(siteId, announcement.id));
  };

  // Handle Powered By TOMIS click tracking
  const handlePoweredByClick = React.useCallback(() => {
    trackEvent("clicked_powered_by_tomis");
  }, []);

  // Show the typing indicator for either a pending bot reply or an operator
  // typing during a live-chat handoff.
  const isTyping = isLoading || agentTyping;

  if (embed) {
    return (
      <ChatOpen
        description={config.description}
        image={config.image}
        name={config.name}
        watermark={config.watermark}
        poweredByName={config.poweredByName}
        poweredByUrl={config.poweredByUrl}
        embed={true}
        feedback={feedback}
        handleSend={handleSend}
        onType={notifyTyping}
        restartSession={restartSession}
        closeWidget={closeWidget}
        turns={turns}
        isTyping={isTyping}
        forms={forms}
        activeFormKey={activeFormKey}
        closeForm={closeForm}
        updateFormValues={updateFormValues}
        submitForm={submitForm}
        onPoweredByClick={handlePoweredByClick}
      />
    );
  }

  const ids = getChatDomIds();

  return (
    <Container
      id={ids.host}
      $withChat={isOpen}
      $isHidden={isHidden}
      $zIndex={config.zIndex}
    >
      <LauncherContainer id={ids.closed} style={launcherStyle}>
        {announcement && announcementReady ? (
          <Announcement
            key={announcement.id}
            announcement={announcement}
            position={config.position}
            onOpen={handleOpenAndLaunch}
            onSend={handleSend}
            onDismiss={onDismiss}
          />
        ) : null}
        <Proactive
          side={config.position}
          messages={proactiveMessages}
          onMessageClick={handleOpenAndLaunch}
        />
        <Launcher
          onClick={handleOpenAndLaunch}
          image={config.launcher}
          size={config.size}
        />
      </LauncherContainer>
      <ChatContainer id={ids.open} style={chatWindowStyle}>
        <ChatOpen
          description={config.description}
          image={config.image}
          name={config.name}
          watermark={config.watermark}
          poweredByName={config.poweredByName}
          poweredByUrl={config.poweredByUrl}
          embed={false}
          feedback={feedback}
          closeWidget={closeWidget}
          restartSession={restartSession}
          handleSend={handleSend}
          onType={notifyTyping}
          turns={turns}
          isTyping={isTyping}
          forms={forms}
          activeFormKey={activeFormKey}
          closeForm={closeForm}
          updateFormValues={updateFormValues}
          submitForm={submitForm}
          onPoweredByClick={handlePoweredByClick}
        />
      </ChatContainer>
    </Container>
  );
};
